import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Customer match test request schema
 */
export const TestCustomerMatchRequestSchema = z.object({
  inboundEmailId: z.uuid("Invalid inbound email ID format"),
});

/**
 * Customer match test response schema
 */
export const TestCustomerMatchResponseSchema = z.object({
  success: z.boolean(),
  match: z
    .object({
      customerId: z.uuid(),
      customerName: z.string(),
      confidence: z.enum(["high", "medium", "low"]),
      reasoning: z.string(),
      matchScore: z.number().min(0).max(1).optional(),
    })
    .optional(),
  error: z.string().optional(),
  metrics: z
    .object({
      totalTime: z.number(),
      pineconeQueryTime: z.number(),
      customerEnrichmentTime: z.number(),
      aiProcessingTime: z.number(),
      pineconeResultsCount: z.number(),
      highConfidenceMatch: z.boolean(),
      aiUsed: z.boolean(),
    })
    .optional(),
});

/**
 * Test customer matching contract
 */
export const testCustomerMatchContract = oc
  .input(TestCustomerMatchRequestSchema)
  .output(TestCustomerMatchResponseSchema)
  .route({
    method: "POST",
  });

export type TestCustomerMatchRequest = z.infer<
  typeof TestCustomerMatchRequestSchema
>;
export type TestCustomerMatchResponse = z.infer<
  typeof TestCustomerMatchResponseSchema
>;

/** Admin test emails: internal domains only (mirrors backend guard). */
const gentlyInternalTestRecipientEmail = z
  .string()
  .email()
  .refine(
    (value) => {
      const e = value.trim().toLowerCase();
      return e.endsWith("@gently-ai.com") || e.endsWith("@gentlyhq.com");
    },
    { message: "Recipient must be @gently-ai.com or @gentlyhq.com" },
  )
  .describe("Internal test recipient (@gently-ai.com or @gentlyhq.com)");

/**
 * Test UPN write access request schema
 */
export const TestUpnWriteAccessRequestSchema = z.object({
  mailboxUpn: z.string().email().describe("The mailbox UPN to send from"),
  senderAddress: z
    .string()
    .email()
    .optional()
    .describe("Optional visible From address when sending as a group or alias"),
  recipientEmail: gentlyInternalTestRecipientEmail,
});

/**
 * Test UPN write access response schema
 */
export const TestUpnWriteAccessResponseSchema = z.object({
  success: z.boolean(),
  mailboxUpn: z.string(),
  senderAddress: z.string().optional(),
  recipientEmail: z.string(),
  error: z.string().optional(),
});

/**
 * Test UPN write access contract — admin-only, sends a test email
 * from the given UPN to an internal address to verify send permissions
 */
export const testUpnWriteAccessContract = oc
  .input(TestUpnWriteAccessRequestSchema)
  .output(TestUpnWriteAccessResponseSchema)
  .route({
    method: "POST",
  });

export type TestUpnWriteAccessRequest = z.infer<
  typeof TestUpnWriteAccessRequestSchema
>;
export type TestUpnWriteAccessResponse = z.infer<
  typeof TestUpnWriteAccessResponseSchema
>;

/**
 * Test forward of an existing Graph message (same path as workflow
 * `send_email` with `forwardFromGraphMessageId`). Admin-only.
 */
export const TestForwardOriginalGraphMessageRequestSchema = z.object({
  mailboxUpn: z
    .string()
    .email()
    .describe("Mailbox UPN that owns the original message in Graph"),
  graphMessageId: z
    .string()
    .min(1)
    .describe(
      "Microsoft Graph message id of the message in that mailbox (e.g. from inbound email ingest)",
    ),
  recipientEmail: gentlyInternalTestRecipientEmail,
  senderAddress: z
    .string()
    .email()
    .optional()
    .describe("Optional visible From when sending as group or alias"),
  subject: z
    .string()
    .optional()
    .describe("Optional subject override for the forwarded message"),
  commentHtml: z
    .string()
    .max(200_000)
    .optional()
    .describe(
      "Optional HTML prepended above the quoted original (Graph forward comment). Omit or leave empty for the default test intro.",
    ),
});

export const TestForwardOriginalGraphMessageResponseSchema = z.object({
  success: z.boolean(),
  mailboxUpn: z.string(),
  graphMessageId: z.string(),
  recipientEmail: z.string(),
  outboundEmailId: z.uuid().optional(),
  error: z.string().optional(),
});

export const testForwardOriginalGraphMessageContract = oc
  .input(TestForwardOriginalGraphMessageRequestSchema)
  .output(TestForwardOriginalGraphMessageResponseSchema)
  .route({
    method: "POST",
  });

export type TestForwardOriginalGraphMessageRequest = z.infer<
  typeof TestForwardOriginalGraphMessageRequestSchema
>;
export type TestForwardOriginalGraphMessageResponse = z.infer<
  typeof TestForwardOriginalGraphMessageResponseSchema
>;

export const TestListRecentMailboxMessagesRequestSchema = z.object({
  mailboxUpn: z.string().email(),
  top: z.number().int().min(1).max(50).optional(),
});

export const RecentMailboxMessageItemSchema = z.object({
  id: z.string(),
  subject: z.string().nullable(),
  receivedDateTime: z.string().nullable(),
  fromAddress: z.string().nullable(),
  internetMessageId: z.string().nullable(),
});

export const TestListRecentMailboxMessagesResponseSchema = z.object({
  success: z.boolean(),
  mailboxUpn: z.string(),
  messages: z.array(RecentMailboxMessageItemSchema),
  error: z.string().optional(),
});

export const testListRecentMailboxMessagesContract = oc
  .input(TestListRecentMailboxMessagesRequestSchema)
  .output(TestListRecentMailboxMessagesResponseSchema)
  .route({
    method: "POST",
  });

export type TestListRecentMailboxMessagesRequest = z.infer<
  typeof TestListRecentMailboxMessagesRequestSchema
>;
export type TestListRecentMailboxMessagesResponse = z.infer<
  typeof TestListRecentMailboxMessagesResponseSchema
>;
